import { Link as RouterLink } from 'react-router-dom';
import { useState } from 'react';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// project imports
import { useTranslation } from 'contexts/LanguageContext';

// assets
import EyeInvisibleOutlined from '@ant-design/icons/EyeInvisibleOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import loginHero from 'assets/images/auth/login-hero.jpg';

export default function ResetPasswordPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { t } = useTranslation();

  return (
    <Grid
      container
      spacing={0}
      sx={{
        width: '100%',
        minHeight: { xs: 'auto', md: '100%' },
        height: { md: '100%' },
        alignContent: { xs: 'flex-start', md: 'stretch' },
        overflow: { xs: 'visible', md: 'hidden' }
      }}
    >
      <Grid
        size={{ xs: 12, md: 5, lg: 4 }}
        sx={{
          order: { xs: 2, md: 1 },
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          px: { xs: 2, sm: 4, md: 6, lg: 8 },
          py: { xs: 2, md: 4 },
          minHeight: { xs: 'auto', md: 0 },
          overflow: { xs: 'visible', md: 'auto' }
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            p: { xs: 3, sm: 4 },
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper'
          }}
        >
          <Stack spacing={3}>
            <Stack spacing={0.75} sx={{ mb: 0.5 }}>
              <Typography variant="h3">{t('Reset password')}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('Create a new password for your BHS account.')}
              </Typography>
            </Stack>

            <Grid container spacing={2.5}>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel htmlFor="new-password">{t('New password')}</InputLabel>
                  <OutlinedInput
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    label={t('New password')}
                    fullWidth
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? t('Hide password') : t('Show password')}
                          color="secondary"
                          onClick={() => setShowPassword((value) => !value)}
                          edge="end"
                        >
                          {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel htmlFor="confirm-new-password">{t('Confirm password')}</InputLabel>
                  <OutlinedInput
                    id="confirm-new-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    label={t('Confirm password')}
                    fullWidth
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showConfirmPassword ? t('Hide password') : t('Show password')}
                          color="secondary"
                          onClick={() => setShowConfirmPassword((value) => !value)}
                          edge="end"
                        >
                          {showConfirmPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>

              <Grid size={12}>
                <Button component={RouterLink} to="/login" fullWidth size="large" variant="contained">
                  {t('Reset password')}
                </Button>
              </Grid>

              <Grid size={12}>
                <Stack direction="row" sx={{ justifyContent: 'center' }}>
                  <Link component={RouterLink} to="/login" variant="body1" underline="hover" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                    {t('Back to login')}
                  </Link>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      </Grid>

      <Grid
        size={{ xs: 12, md: 7, lg: 8 }}
        sx={{
          order: { xs: 1, md: 2 },
          display: 'block',
          height: { xs: 180, sm: 240, md: '100%' },
          minHeight: { xs: 180, sm: 240, md: 0 }
        }}
      >
        <Box
          component="img"
          src={loginHero}
          alt="Business planning workspace"
          sx={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'cover',
            objectPosition: { xs: 'center 42%', md: 'center' },
            borderRadius: 0
          }}
        />
      </Grid>
    </Grid>
  );
}
